import { useEffect, FC, useMemo, useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CurrencyEnum } from '@spectrocoin/sc-currencies';
import Loader from '../../components/Loader/Loader';
import Content from '../../components/Content/Content';
import FormError from '../../components/FormError/FormError';
import { useAppDispatch } from '../../redux/Store';
import { RemoteData, RemoteStatus } from '../../redux/SharedTypes';
import { fetchButton, fetchCurrencies } from '../../redux/DonationSlice/DonationThunks';
import { getButton, getCurrencies } from '../../redux/DonationSlice/DonationSelectors';
import DonationView from './DonationView/DonationView';
import { getErrorMessageOrDefault } from '../../helpers/errorMessageHelper/errorMessageHelper';
import DonationContext from './Context/DonationContext';
import { DonationStage } from '../../redux/DonationSlice/DonationSlice';
import DonationAddress from '../../interfaces/DonationAddress';
import axiosInstance from '../../helpers/axiosInstance';
import { getDonationButtonCreateAddressURL } from '../../endpoints';

const messages = defineMessages({
	noPayOptionsAvailable: {
		id: 'payments.no_payment_options_available',
		defaultMessage: 'No payment options are available',
	},
});

const Donation: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const { id = '' } = useParams<{ id?: string }>();
	const { status: buttonStatus, error: buttonError } = useSelector(getButton);
	const {
		status: currenciesStatus,
		error: currenciesError,
		data: currenciesData,
	} = useSelector(getCurrencies);
	const [stage, setStage] = useState(DonationStage.None);
	const [address, setAddress] = useState<RemoteData<DonationAddress>>({
		status: RemoteStatus.None,
	});

	const content = useMemo(() => {
		if ([buttonStatus, currenciesStatus].includes(RemoteStatus.InProgress)) return <Loader />;
		if (
			[buttonStatus, currenciesStatus].includes(RemoteStatus.Error) ||
			currenciesData?.length === 0
		) {
			const noPayOptionsAvailableMessage =
				currenciesData?.length === 0 ? messages.noPayOptionsAvailable : '';
			return (
				<Content showBadge={false}>
					<FormError
						message={formatMessage(
							getErrorMessageOrDefault(
								buttonError || currenciesError || noPayOptionsAvailableMessage
							)
						)}
					/>
				</Content>
			);
		}
		if ([buttonStatus, currenciesStatus].every((x) => x === RemoteStatus.Done))
			return <DonationView />;

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [buttonStatus, currenciesStatus]);

	const createAddress = useCallback(
		(currency: CurrencyEnum) => {
			setAddress({ status: RemoteStatus.InProgress });
			return axiosInstance
				.post<DonationAddress>(getDonationButtonCreateAddressURL(), {
					payCurrencyCode: currency,
					paymentButtonId: id,
				})
				.then(({ data }) => {
					setAddress({ status: RemoteStatus.Done, data });
				})
				.catch((e) => {
					setAddress({ status: RemoteStatus.Error, error: e.response?.data });
					throw e;
				});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[id]
	);

	useEffect(() => {
		if (buttonStatus === RemoteStatus.None) dispatch(fetchButton(id));
	}, [dispatch, id, buttonStatus]);

	useEffect(() => {
		if (currenciesStatus === RemoteStatus.None) dispatch(fetchCurrencies(id));
	}, [dispatch, id, currenciesStatus]);

	// eslint-disable-next-line react/jsx-no-constructed-context-values
	const context = { stage, setStage, address, setAddress, createAddress };

	return <DonationContext.Provider value={context}>{content}</DonationContext.Provider>;
};

export default Donation;
